/* ----------------------------------------------------------------------------
 * Pure model for the human FINAL edit of a READY blog draft (owner directive
 * item 2). DEPENDENCY-FREE — no react, no api client, no browser globals — so it
 * unit-tests under the repo's `node --test --experimental-strip-types` runner,
 * mirroring the contentActionModel.ts / connections-model.ts split.
 *
 * It encodes the SAME contract the backend ContentDraftEditor enforces, so the
 * FE validates a final edit before the PATCH and never sends an obviously
 * invalid one:
 *   - ONLY the four human-authored fields are editable (title / slug /
 *     meta_title / body_md). Every other key is dropped — a human edit can never
 *     touch focus_keyphrase, the featured image, the SEO target, links, meta
 *     description, provenance or status. Those controls stay server-owned.
 *   - Length caps mirror the backend guards EXACTLY. The server is still the
 *     authority (it re-runs the full ready/SEO/Yoast validator); this is a
 *     pre-flight so the user gets an instant, honest message.
 * -------------------------------------------------------------------------- */

/** The ONLY fields a human may edit on a ready draft. */
export const BLOG_EDIT_FIELDS = ['title', 'slug', 'meta_title', 'body_md'] as const;
export type BlogEditField = (typeof BLOG_EDIT_FIELDS)[number];

/** A partial edit payload (all optional; send only what changed). */
export type BlogEditableFields = Partial<Record<BlogEditField, string>>;

/** A full, defined set of the four fields (used to seed the edit form). */
export type BlogEditForm = Record<BlogEditField, string>;

/** Client length caps — mirror bookedcentral-api ContentDraftEditor exactly. */
export const BLOG_EDIT_LIMITS: Record<BlogEditField, number> = {
  title: 300,
  slug: 200,
  meta_title: 200,
  body_md: 60000,
};

/** Seed an edit form from a draft-shaped object, taking ONLY the four fields
 *  (missing/non-string values default to ''). */
export function blogEditInitial(draft: Partial<Record<BlogEditField, unknown>>): BlogEditForm {
  const out = {} as BlogEditForm;
  for (const f of BLOG_EDIT_FIELDS) {
    out[f] = typeof draft[f] === 'string' ? (draft[f] as string) : '';
  }
  return out;
}

/** Whitelist an arbitrary object to the four editable STRING fields, dropping
 *  everything else (mirrors the server's field whitelist). */
export function pickBlogEdits(input: Record<string, unknown>): BlogEditableFields {
  const out: BlogEditableFields = {};
  for (const f of BLOG_EDIT_FIELDS) {
    if (typeof input[f] === 'string') out[f] = input[f] as string;
  }
  return out;
}

/** Only the fields whose value differs from the current draft (so a Save sends a
 *  minimal, honest patch). Compares against the seeded form values. */
export function blogEditChanges(current: BlogEditForm, next: BlogEditForm): BlogEditableFields {
  const out: BlogEditableFields = {};
  for (const f of BLOG_EDIT_FIELDS) {
    if (next[f] !== current[f]) out[f] = next[f];
  }
  return out;
}

/** The first field that exceeds its cap, or null when all are within limits.
 *  (Uses Array.from length so multi-byte characters count like the PHP mb_strlen
 *  guard closely enough for a pre-flight.) */
export function firstBlogEditLengthError(
  fields: BlogEditableFields,
): { field: BlogEditField; limit: number } | null {
  for (const f of BLOG_EDIT_FIELDS) {
    const v = fields[f];
    if (typeof v === 'string' && Array.from(v).length > BLOG_EDIT_LIMITS[f]) {
      return { field: f, limit: BLOG_EDIT_LIMITS[f] };
    }
  }
  return null;
}
